import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEffect } from "react";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Image as ImageIcon, Link2, AlignLeft, AlignCenter, AlignRight, Palette,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

/**
 * TipTapParagraph — a compact rich-text editor for paragraph blocks.
 * Supports inline images with left/center/right alignment (text wraps
 * around left/right images) and drag-and-drop upload to object storage.
 */
export default function TipTapParagraph({ html, onChange, testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      TextStyle,
      Color,
      Image.configure({ inline: true, allowBase64: false, HTMLAttributes: {} }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: html || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  // Keep external -> internal in sync when parent replaces the block
  useEffect(() => {
    if (editor && html !== undefined && html !== editor.getHTML()) {
      editor.commands.setContent(html || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html]);

  const uploadImage = async (file, align = "center") => {
    const form = new FormData(); form.append("file", file);
    try {
      const r = await axios.post(`${API}/litlab/uploads`, form,
        { headers: { ...httpAuth() } });
      const url = r.data.url;
      if (!url) throw new Error("no url");
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      // Apply alignment via inline style on the just-inserted image
      const styleFor = align === "left"
        ? "float:left;margin:.25rem 1rem .5rem 0;max-width:55%"
        : align === "right"
          ? "float:right;margin:.25rem 0 .5rem 1rem;max-width:55%"
          : "display:block;margin:1rem auto;max-width:100%";
      // Move cursor back one so the last inserted image is targetable
      editor.commands.updateAttributes("image", { style: styleFor });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const pickFile = (align = "center") => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = () => { if (inp.files?.[0]) uploadImage(inp.files[0], align); };
    inp.click();
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f && /^image\//.test(f.type)) uploadImage(f, "center");
  };

  const addLink = () => {
    const url = window.prompt("URL (leave empty to unlink):", editor.getAttributes("link")?.href || "");
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const setColor = () => {
    const c = window.prompt("Text color (hex or CSS name):", editor.getAttributes("textStyle")?.color || "#0f172a");
    if (c === null) return;
    if (!c) editor.chain().focus().unsetColor().run();
    else editor.chain().focus().setColor(c).run();
  };

  if (!editor) return null;

  const ToolBtn = ({ onClick, active, icon: Icon, title, testid: t }) => (
    <button type="button" onClick={onClick} title={title} data-testid={t}
      className={`p-1.5 rounded text-xs ${active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
      <Icon className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div className="rounded-md ring-1 ring-slate-300 bg-white" data-testid={testid || "tiptap-editor"}>
      <div className="flex flex-wrap gap-0.5 border-b border-slate-200 p-1 sticky top-0 bg-white z-10">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()}
                  active={editor.isActive("bold")} icon={Bold} title="Bold" testid="tt-bold" />
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()}
                  active={editor.isActive("italic")} icon={Italic} title="Italic" testid="tt-italic" />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  active={editor.isActive("heading", { level: 2 })} icon={Heading2} title="H2" testid="tt-h2" />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  active={editor.isActive("heading", { level: 3 })} icon={Heading3} title="H3" testid="tt-h3" />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()}
                  active={editor.isActive("bulletList")} icon={List} title="Bullet list" testid="tt-ul" />
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  active={editor.isActive("orderedList")} icon={ListOrdered} title="Numbered" testid="tt-ol" />
        <ToolBtn onClick={addLink} active={editor.isActive("link")} icon={Link2} title="Link" testid="tt-link" />
        <ToolBtn onClick={setColor} active={!!editor.getAttributes("textStyle")?.color}
                  icon={Palette} title="Color" testid="tt-color" />
        <div className="w-px bg-slate-200 mx-1" />
        <ToolBtn onClick={() => pickFile("left")}   icon={AlignLeft}   title="Image · wrap left" testid="tt-img-left" />
        <ToolBtn onClick={() => pickFile("center")} icon={ImageIcon}   title="Image · centered"  testid="tt-img-center" />
        <ToolBtn onClick={() => pickFile("right")}  icon={AlignRight}  title="Image · wrap right" testid="tt-img-right" />
      </div>
      <div onDrop={onDrop} onDragOver={(e) => e.preventDefault()}
           className="p-3 min-h-[120px] tiptap-content"
           data-testid={`${testid || "tiptap-editor"}-area`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
